import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { ftthAreaMapaService, FtthAreaMapa } from '../../services/ftthAreaMapaService';
import EscritorioSelect from '../selects/EscritorioSelect';

const COLOR_PALETTE = [
  '#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4',
  '#8BC34A', '#FF5722', '#673AB7', '#009688', '#E91E63', '#3F51B5',
];

export interface FtthAreaMapaFormProps {
  coordenadas: Array<{ lat: number; lng: number }>;
  onSuccess?: (area: FtthAreaMapa) => void;
  onCancel?: () => void;
  area?: FtthAreaMapa;
  isFullscreen?: boolean;
}

interface FtthAreaMapaFormData {
  nome: string;
  escritorio_id: number;
  cor: string;
  informacoes: string;
}

const FtthAreaMapaForm: React.FC<FtthAreaMapaFormProps> = ({
  coordenadas,
  onSuccess,
  onCancel,
  area,
  isFullscreen,
}) => {
  const [formData, setFormData] = useState<FtthAreaMapaFormData>({
    nome: area?.nome ?? '',
    escritorio_id: area?.escritorio_id ?? 0,
    cor: area?.cor ?? '#2196F3',
    informacoes: area?.informacoes ?? '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof FtthAreaMapaFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEscritorioChange = (value: number | number[] | null) => {
    const escritorioId = Array.isArray(value) ? (value[0] ?? null) : value;
    setFormData((prev) => ({ ...prev, escritorio_id: escritorioId ?? 0 }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.nome.trim()) {
      setError('Nome é obrigatório');
      return;
    }
    if (!formData.escritorio_id) {
      setError('Escritório é obrigatório');
      return;
    }
    if (coordenadas.length < 3) {
      setError('O polígono deve ter pelo menos 3 pontos');
      return;
    }

    setLoading(true);
    try {
      if (area?.id) {
        const updated = await ftthAreaMapaService.update(area.id, {
          nome: formData.nome.trim(),
          coordenadas,
          cor: formData.cor || '#2196F3',
          escritorio_id: formData.escritorio_id,
          informacoes: formData.informacoes?.trim() || null,
        });
        onSuccess?.(updated);
      } else {
        const created = await ftthAreaMapaService.create({
          nome: formData.nome.trim(),
          coordenadas,
          cor: formData.cor || '#2196F3',
          escritorio_id: formData.escritorio_id,
          informacoes: formData.informacoes?.trim() || null,
        });
        onSuccess?.(created);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao salvar área');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body2" color="text.secondary">
        Polígono com {coordenadas.length} pontos
      </Typography>
      <TextField
        label="Nome da área"
        value={formData.nome}
        onChange={handleChange('nome')}
        required
        fullWidth
        disabled={loading}
      />
      <EscritorioSelect
        value={formData.escritorio_id || null}
        onChange={handleEscritorioChange}
        label="Escritório"
        required
        disabled={loading}
        disablePortal={!!isFullscreen}
      />
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Cor do polígono
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {COLOR_PALETTE.map((c) => (
            <Box
              key={c}
              onClick={() => setFormData((p) => ({ ...p, cor: c }))}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                bgcolor: c,
                border: formData.cor === c ? 3 : 1,
                borderColor: formData.cor === c ? 'primary.main' : 'divider',
                cursor: 'pointer',
              }}
            />
          ))}
        </Box>
      </Box>
      <TextField
        label="Informações (opcional)"
        value={formData.informacoes}
        onChange={handleChange('informacoes')}
        multiline
        rows={2}
        fullWidth
        disabled={loading}
      />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Salvar'}
        </Button>
      </Box>
    </Box>
  );
};

export default FtthAreaMapaForm;
